/**
 * Widget theme for React Native.
 *
 * Deliberately thin: the theme VALUES (defaults, presets) and the merge/
 * contrast LOGIC both come from `@ringg/core` — `mergeWidgetTheme` here is
 * core's function, not a copy. The web package still carries its own copy of
 * that table for byte-fidelity with agents-cdn; RN has no such legacy to match
 * and takes the shared one, so a token change reaches this platform through
 * codegen (AGENTS.md rule 2).
 *
 * What IS local is unit translation. The theme surface is CSS-shaped
 * (`"16px"`, `"9999px"`, `fontFamily: "inherit"`) because that's the contract
 * integrators already write against on web; RN needs numbers and a real family
 * name, so the `resolve*` helpers below are the single conversion point.
 */

import { createContext, useContext, useMemo, type FC, type ReactNode } from "react";
import { DEFAULT_WIDGET_THEME, getButtonRadius, mergeWidgetTheme, type ButtonStyle, type ResolvedWidgetTheme, type WidgetTheme } from "@ringg/core";

interface WidgetThemeProviderProps {
  theme?: WidgetTheme;
  children: ReactNode;
}

/** CSS's "no explicit family — use whatever the platform gives you". */
const INHERIT = "inherit";

/**
 * `borderRadius: "9999px"` is the web idiom for a pill. RN clamps the radius
 * to half the shorter side anyway, so any large number reads as a pill — but
 * it must be finite: `Infinity` crashes the shadow-node layout pass.
 */
const PILL_RADIUS = 9999;

const WidgetThemeContext = createContext<ResolvedWidgetTheme>(DEFAULT_WIDGET_THEME);

/** `"16px"` → `16`. Unparseable values fall back rather than laying out as NaN. */
export const resolveRadius = (value: string | undefined, fallback = 0): number => {
  if (value === undefined) return fallback;
  const numeric = Number(value.trim().endsWith("px") ? value.trim().slice(0, -2) : value.trim());
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(numeric, PILL_RADIUS);
};

/** Button corner radius as an RN number, from core's shared style → CSS mapping. */
export const resolveButtonRadius = (style: ButtonStyle | undefined): number => resolveRadius(getButtonRadius(style));

/**
 * `"inherit"` has no meaning in RN — there is no cascade. Returning undefined
 * lets `<Text>` fall through to the platform system font, which is what
 * "inherit" resolves to on web for an unstyled host page.
 */
export const resolveFontFamily = (fontFamily: string | undefined): string | undefined => (!fontFamily || fontFamily === INHERIT ? undefined : fontFamily);

export const WidgetThemeProvider: FC<WidgetThemeProviderProps> = ({ theme, children }) => {
  const merged = useMemo(() => mergeWidgetTheme(theme), [theme]);
  return <WidgetThemeContext.Provider value={merged}>{children}</WidgetThemeContext.Provider>;
};

export const useWidgetTheme = (): ResolvedWidgetTheme => useContext(WidgetThemeContext);
